import * as React from "react";

interface GenS3FileUploaderProps {
  id?: string;
  accept?: string;
  signingUrl: string;
  onProgress?: (percent: number) => void;
  onError?: (error: string) => void;
  onFinish?: () => void;
  documentId: number;
  mediaOptions?: {is_silent: true};
  mediaType: MediaType,
}

export default function GenS3MediaAssetUploader(props: GenS3FileUploaderProps) {
  const {
    id,
    accept,
    onProgress,
    onError,
    onFinish,
    documentId,
    mediaOptions={},
    mediaType,
  } = props;

  const createMediaAsset = React.useCallback((signedUrl: string) => {
    fetch(`/documents/${documentId}/media_assets`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
       },
      body: JSON.stringify({
        media_asset: {
          original_upload_path: (new URL(signedUrl).pathname),
          media_type: mediaType,
          ...mediaOptions,
        },
      }),
     })
     .then((response) => {
      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      if (onFinish) onFinish();
     })
     .catch((error) => {
      if (onError) onError(error);
     });
   }, [documentId, mediaType, onError, onFinish]);

  const handleOnChange = React.useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) { return; }

    const file = fileList.item(0);
    if (!file) { return; }

    try {
      const scrubedName = file.name.replace(/[^\w\d_\-\.]+/ig, "");

      const queryArguments = {
        path: "/uploads/",
        objectName: scrubedName,
        contentType: file.type,
      };

      const signedUrlResponse = await fetch(`/media_assets/signed_url?${new URLSearchParams(queryArguments)}`);

      if (!signedUrlResponse.ok) throw new Error("Failed to get signed URL");

      const { signedUrl } = await signedUrlResponse.json();

      const xhr = new XMLHttpRequest();
      xhr.open("PUT", signedUrl);

      xhr.upload.addEventListener("progress", function (e) {
        if (e.lengthComputable) {
          const percent = (e.loaded / e.total) * 100;
          if (onProgress) onProgress(percent);
        }
      });

      xhr.onerror = function () {
        throw new Error("Upload failed");
      };

      xhr.onload = function () {
        if (xhr.status === 200) {
          createMediaAsset(signedUrl);
        } else {
          throw new Error(`Upload Error: S3 responsed with ${xhr.status}`);
        }
      };

      xhr.setRequestHeader("Content-Type", file.type);

      xhr.send(file);
    } catch (error) {
      if (error instanceof Error) {
        if (onError) onError(error.message);
      } else {
        if (onError) onError("Upload Error");
      }
    }
  }, [createMediaAsset, onProgress, onError]);

  return (
    <input
      id={id}
      type='file'
      accept={accept}
      onChange={(e) => handleOnChange(e).then(() => {})}
      style={{display: "none"}}
    />
  );
}
